import { Component } from 'react'
import onlyUpdateForKeys from 'recompose/onlyUpdateForKeys'
import 'd3-transition'
import { scaleLinear, scaleTime, ScaleLinear, ScaleTime } from 'd3-scale'
import { select, Selection } from 'd3-selection'
import { extent, max } from 'd3-array'
import { line as d3Line, Line } from 'd3-shape'
import { axisBottom, axisLeft, Axis } from 'd3-axis'
import { timeFormat, timeParse } from 'd3-time-format'
import { easeQuadOut } from 'd3-ease'
import { format } from 'd3-format'
import { Loader } from 'shared/components/preloader-animation'
import { singularContentTypes } from '../../../site/user-dashboard-analytics/app'
import css from './line-chart.css'
import { giphyIndigo, giphyPink } from '@giphy/colors'
import { getDashboard } from './networking'

type TimeSeriesKey = string

interface Point {
    viewCount: number
    date: Date
}

interface Props {
    activeTimeSeries: TimeSeriesKey
    isMobile?: boolean
    contentType?: string
    dateRange: [Date, Date]
    onLoad?: () => void
    user: { username: string }
}

interface State {
    isFetching: boolean
    data: Record<TimeSeriesKey, Point[]>
    contentType: string
}

class GraphAnalyticsBase extends Component<Props, State> {
    state: State = {
        isFetching: false,
        data: {},
        contentType: 'GIFs & Stickers',
    }

    parseTime = timeParse('%Y-%m-%d') as (s: string) => Date | null
    monthDayFormat = timeFormat('%m/%d')
    monthDayYearFormat = timeFormat('%x')
    monthYearFormat = timeFormat('%m/%Y')
    monthYearMobileFormat = timeFormat('%m/%y')

    numberFormat = format(',')
    dotRadius = 10
    dotInnerRadius = 4
    bubbleHeight = 22

    // d3 components
    dimensions: DOMRect = new DOMRect()
    margin = { top: 20, right: 10, bottom: 40, left: 50 }
    width = 0
    height = 0
    x: ScaleTime<number, number> | null = null
    y: ScaleLinear<number, number> | null = null
    valueLine: Line<Point> | null = null
    xAxis: Axis<Date | number> | null = null
    yAxis: Axis<number> | null = null
    container: HTMLElement | null = null
    svg: Selection<SVGSVGElement, unknown, null, undefined> | null = null
    toolTip: Selection<HTMLDivElement, unknown, HTMLElement, unknown> | null = null

    getDashboard() {
        const {
            user: { username },
            contentType,
            dateRange,
        } = this.props

        Promise.resolve(() => this.setState({ isFetching: true }))
            .then(() => getDashboard(contentType || this.state.contentType, dateRange, username))
            .then((data: Array<{ viewCount: number; date: string }>) => this.processData(data))
            .then((data) => this.setStateData(data))
            .then(() => this.setState({ isFetching: false }))
            .then(() => this.updateChart())
            .catch(() => this.setState({ isFetching: false }))
            .finally(() => {
                this.props.onLoad && this.props.onLoad()
            })
    }

    setStateData(newData: Point[]) {
        return new Promise<void>((resolve) => {
            this.setState({ data: { ...this.state.data, [this.props.activeTimeSeries]: newData } }, () => resolve())
        })
    }

    processData(data: Array<{ viewCount: number; date: string }>) {
        const parsedData = data.map(({ viewCount, date }) => ({
            viewCount: Math.max(0, viewCount),
            date: this.parseTime(date) as Date,
        }))
        return parsedData.slice().sort(({ date: dateA }, { date: dateB }) => +dateA - +dateB)
    }
    getTimeSeries(activeTimeSeries: TimeSeriesKey) {
        return this.state.data[activeTimeSeries] || []
    }

    componentDidUpdate(prevProps: Props) {
        const { contentType, dateRange, activeTimeSeries } = prevProps
        const { isFetching } = this.state
        if (this.props.contentType !== contentType) {
            this.setState({ contentType: this.props.contentType || '' })
            this.getDashboard()
        }
        if (isFetching) {
            return
        }
        const newTimeFrame = this.props.dateRange !== dateRange
        const newTimeSeries = this.props.activeTimeSeries !== activeTimeSeries
        if (newTimeFrame || newTimeSeries) {
            return this.getDashboard()
        }
    }

    componentDidMount() {
        this.getDashboard()
        this.toolTip = select('body').append('div').classed(css.toolTip, true).style('opacity', 0)
        if (this.container) {
            this.dimensions = this.container.getBoundingClientRect()
        }
        let leftMargin = this.props.isMobile ? 50 : 50
        this.margin = { top: 20, right: 10, bottom: 40, left: leftMargin }
        this.width = this.dimensions.width - this.margin.left - this.margin.right
        this.height = this.dimensions.height - this.margin.top - this.margin.bottom
        this.x = scaleTime().range([0, this.width])
        this.y = scaleLinear().range([this.height, 0])
        this.valueLine = d3Line<Point>()
            .x(({ date }: Point) => (this.x as ScaleTime<number, number>)(date))
            .y(({ viewCount }: Point) => (this.y as ScaleLinear<number, number>)(viewCount))

        const { xAxis, yAxis } = this.getAxes(this.x, this.y, this.width)
        this.xAxis = xAxis
        this.yAxis = yAxis
        this.createChart()
    }
    componentWillUnmount() {
        this.toolTip.remove()
    }
    addGradient(defs: Selection<SVGDefsElement, unknown, null, undefined>) {
        const gradient = defs.append('linearGradient').attr('id', 'svgGradient').attr('x1', '0%').attr('x2', '100%')

        gradient
            .append('stop')
            .attr('class', 'start')
            .attr('offset', '0%')
            .attr('stop-color', giphyIndigo)
            .attr('stop-opacity', 1)

        gradient
            .append('stop')
            .attr('class', 'end')
            .attr('offset', '100%')
            .attr('stop-color', giphyPink)
            .attr('stop-opacity', 1)
    }
    getAxes(
        xScale: ScaleTime<number, number>,
        yScale: ScaleLinear<number, number>,
        width: number
    ): { xAxis: Axis<Date | number>; yAxis: Axis<number> } {
        const { isMobile, activeTimeSeries } = this.props
        let numberOfTicks = isMobile ? 3 : 5
        var xAxisPadding = this.props.isMobile ? 10 : 20
        let xAxis = axisBottom(xScale)
            .ticks(numberOfTicks)
            .tickPadding(xAxisPadding)
            .tickFormat(this.determineDateFormat(activeTimeSeries))
        let yAxis = axisLeft(yScale).tickSize(width).ticks(numberOfTicks, 's').tickPadding(10)
        return { xAxis, yAxis }
    }
    customXAxis(g: Selection<SVGGElement, unknown, null, undefined>, xAxis: Axis<Date | number>) {
        g.transition().duration(750).call(xAxis)
        g.select('.domain').remove()
        g.select('line').remove()
        g.select('path').remove()
    }
    customYAxis(g: Selection<SVGGElement, unknown, null, undefined>, yAxis: Axis<number>) {
        g.transition().duration(750).call(yAxis)
        g.select('.domain').remove()
        g.selectAll('.tick:not(:first-of-type) line').attr('stroke-array', '2,2')
    }
    drawLine(
        data: Point[],
        g: Selection<SVGGElement, unknown, null, undefined>,
        line: Line<Point>,
        toolTip: Selection<HTMLDivElement, unknown, HTMLElement, unknown> | null
    ) {
        const { activeTimeSeries, dateRange } = this.props
        const timeseries = [{ data, type: activeTimeSeries }]
        const lineSelection = g
            .selectAll<SVGPathElement, { data: Point[]; type: TimeSeriesKey }>('.line')
            .data(timeseries)
        var parent = this
        ;(lineSelection as Selection<SVGPathElement, { data: Point[]; type: TimeSeriesKey }, SVGGElement, unknown>)
            .enter()
            .append('path')
            .classed('line', true)
            .attr('d', ({ data }: { data: Point[] }) => line(data))
            .attr('fill', 'none')
            .attr('stroke', 'url(#svgGradient)')
            .attr('stroke-linejoin', 'round')
            .attr('stroke-linecap', 'round')
            .attr('stroke-width', function () {
                return parent.getStrokeWidth(dateRange)
            })
            .attr('stroke-dashoffset', function () {
                return this.getTotalLength()
            })
            .attr('stroke-dasharray', function () {
                return 99999
            })
            .transition()
            .delay(400)
            .duration(750)
            .ease(easeQuadOut)
            .attr('stroke-dashoffset', 0)

        lineSelection
            .transition()
            .duration(750)
            .ease(easeQuadOut)
            .attr('d', ({ data }: Line<Point>) => line(data))
            .attr('stroke-width', function () {
                return parent.getStrokeWidth(dateRange)
            })
            .attr('stroke-dasharray', function () {
                return 99999
            })
            .attr('stroke-dashoffset', 0)

        lineSelection
            .exit()
            .transition()
            .duration(400)
            .ease(easeQuadOut)
            .attr('stroke-width', function () {
                return parent.getStrokeWidth(dateRange)
            })
            .attr('stroke-dashoffset', function () {
                return this.getTotalLength()
            })
            .remove()

        const dataPointSelection = g.selectAll<SVGGElement, Point>('.data-point').data(data, () => activeTimeSeries)
        const dataPointsEnterSet = dataPointSelection
            .enter()
            .append('g')
            .classed('data-point', true)
            .style('opacity', 0)
            .on('mouseenter', this.showToolTip(toolTip))
            .on('mouseleave', this.hideToolTip(toolTip))
        dataPointSelection.exit().remove()

        dataPointsEnterSet.append('circle').classed(css.dot, true)
        const dotSelection = g.selectAll<SVGCircleElement, Point>(`.${css.dot}`)

        dotSelection
            .data(data, () => activeTimeSeries)
            .attr('r', this.dotRadius)
            .attr('cx', ({ date }: Point) => this.x(date))
            .attr('cy', ({ viewCount }: Point) => this.y(viewCount))

        dotSelection
            .attr('r', this.dotRadius)
            .attr('cx', ({ date }: Point) => this.x(date))
            .attr('cy', ({ viewCount }: Point) => this.y(viewCount))

        dataPointsEnterSet.append('circle').classed(css.dotOrbit, true)
        const orbitSelection = g.selectAll<SVGCircleElement, Point>(`.${css.dotOrbit}`)

        orbitSelection
            .data(data, () => activeTimeSeries)
            .attr('r', 0)
            .attr('cx', ({ date }: Point) => this.x(date))
            .attr('cy', ({ viewCount }: Point) => this.y(viewCount))

        orbitSelection
            .attr('r', 0)
            .attr('cx', ({ date }: Point) => this.x(date))
            .attr('cy', ({ viewCount }: Point) => this.y(viewCount))

        dataPointsEnterSet.append('circle').classed(css.dotInner, true)
        const dotsInnerSelection = g.selectAll<SVGCircleElement, Point>(`.${css.dotInner}`)

        dotsInnerSelection
            .data(data, () => activeTimeSeries)
            .attr('r', this.dotInnerRadius)
            .attr('cx', ({ date }: Point) => this.x(date))
            .attr('cy', ({ viewCount }: Point) => this.y(viewCount))

        dotsInnerSelection
            .attr('r', this.dotInnerRadius)
            .attr('cx', ({ date }: Point) => this.x(date))
            .attr('cy', ({ viewCount }: Point) => this.y(viewCount))
    }
    roundedNearestOrderofMagnitude(value: number) {
        if (value < 1) {
            value = 10
        }
        const numOrderOfMagnitude = Math.floor(Math.log10(value))
        const orderOfMagnitude = Math.pow(10, numOrderOfMagnitude)
        return Math.ceil(value / orderOfMagnitude) * orderOfMagnitude
    }
    addAxes(g: Selection<SVGGElement, unknown, null, undefined>) {
        g.append('g').classed(css.xAxis, true).attr('transform', `translate(0, ${this.height})`)
        g.append('g').classed(css.yAxis, true).attr('transform', `translate(${this.width}, 0)`)
    }

    getStrokeWidth(dateRange: [Date, Date]) {
        var lengthInMonths = this.getDateRangeLengthInMonths(dateRange)
        if (lengthInMonths > 9) {
            return '2'
        } else {
            return '6'
        }
    }
    getDateRangeLengthInMonths(dateRange: [Date, Date]) {
        var rangeLength = Math.abs(dateRange[1].getTime() - dateRange[0].getTime())
        var lengthInDays = rangeLength / 1000 / 60 / 60 / 24
        return lengthInDays / 30
    }

    updateAxesScale(activeTimeSeries: TimeSeriesKey) {
        var numberOfTicks = this.props.isMobile ? 3 : 5
        switch (activeTimeSeries) {
            case 'ALL_TIME':
                if (this.props.dateRange) {
                    var rangeLength = Math.abs(this.props.dateRange[1].getTime() - this.props.dateRange[0].getTime())
                    var lengthInDays = rangeLength / 1000 / 60 / 60 / 24
                    var lengthInMonths = lengthInDays / 30

                    if (numberOfTicks > lengthInDays) {
                        numberOfTicks = Math.floor(lengthInDays)
                    }

                    if (lengthInMonths > 6) {
                        if (this.props.isMobile) {
                            this.xAxis.ticks(numberOfTicks).tickFormat(this.monthYearMobileFormat)
                        } else {
                            this.xAxis.ticks(numberOfTicks).tickFormat(this.monthYearFormat)
                        }
                    } else {
                        this.xAxis.ticks(numberOfTicks).tickFormat(this.monthDayFormat)
                    }
                }
                break
            case 'MONTH':
                this.xAxis.ticks(numberOfTicks).tickFormat(this.monthDayFormat)
                break
            case 'WEEK':
                this.xAxis.ticks(numberOfTicks).tickFormat(this.monthDayFormat)
                break
            case 'CUSTOM':
                if (this.props.dateRange) {
                    var rangeLength = Math.abs(this.props.dateRange[1].getTime() - this.props.dateRange[0].getTime())
                    var lengthInDays = rangeLength / 1000 / 60 / 60 / 24
                    var lengthInMonths = lengthInDays / 30

                    if (numberOfTicks > lengthInDays) {
                        numberOfTicks = Math.floor(lengthInDays)
                    }

                    if (lengthInMonths > 6) {
                        this.xAxis.ticks(numberOfTicks).tickFormat(this.monthYearFormat)
                    } else {
                        this.xAxis.ticks(numberOfTicks).tickFormat(this.monthDayFormat)
                    }
                }
                break
            default:
                this.xAxis.ticks(numberOfTicks).tickFormat(this.monthDayYearFormat)
                break
        }
    }
    updateAxes(data: Point[], g: Selection<SVGGElement, unknown, null, undefined>) {
        const { activeTimeSeries, dateRange } = this.props
        let dataToDraw = data.slice()
        if (dataToDraw.length === 0) {
            dataToDraw[0] = { date: dateRange[0], viewCount: 0 }
            dataToDraw[1] = { date: dateRange[1], viewCount: 0.01 }
        }
        this.x!.domain(extent(dataToDraw, ({ date }) => date) as [Date, Date])
        this.updateAxesScale(activeTimeSeries)
        this.y!.domain([0, this.roundedNearestOrderofMagnitude(max(dataToDraw, ({ viewCount }) => viewCount) || 0)])
        g.select(`.${css.xAxis}`).call((g: SVGGElement) => this.customXAxis(g, this.xAxis!))
        g.select(`.${css.yAxis}`).call((g: SVGGElement) => this.customYAxis(g, this.yAxis!))
    }
    createChart() {
        const { activeTimeSeries, dateRange } = this.props
        const data = this.getTimeSeries(activeTimeSeries)

        this.svg!.attr('width', this.dimensions.width).attr('height', this.dimensions.height)
        const defs = this.svg!.append('defs')
        const g = this.svg!.append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`)

        this.addGradient(defs)
        this.addAxes(g)
        this.updateAxes(data, g)
        let dataToDraw = data.slice()
        if (dataToDraw.length === 0) {
            dataToDraw[0] = { date: dateRange[0], viewCount: 0 }
            dataToDraw[1] = { date: dateRange[1], viewCount: 0 }
        }

        this.drawLine(dataToDraw, g, this.valueLine, this.toolTip)
    }
    updateChart() {
        const { activeTimeSeries, dateRange } = this.props
        const data = this.getTimeSeries(activeTimeSeries)
        const g = this.svg!.select<SVGGElement>('g')
        this.updateAxes(data, g)
        let dataToDraw = data.slice()
        if (dataToDraw.length === 0) {
            dataToDraw[0] = { date: dateRange[0], viewCount: 0 }
            dataToDraw[1] = { date: dateRange[1], viewCount: 0 }
        }

        this.drawLine(dataToDraw, g, this.valueLine, this.toolTip)
    }

    determineDateFormat(activeTimeSeries: TimeSeriesKey) {
        switch (activeTimeSeries) {
            case 'ALL_TIME':
                return this.monthYearFormat
            case 'MONTH':
                return this.monthDayFormat
            case 'WEEK':
                return this.monthDayFormat
            case 'CUSTOM':
                return this.monthDayYearFormat
            default:
                return this.monthDayYearFormat
        }
    }

    showToolTip(toolTip: Selection<HTMLDivElement, unknown, HTMLElement, unknown> | null) {
        const { dotRadius, bubbleHeight, monthDayYearFormat, numberFormat } = this
        if (this.props.isMobile) {
            return () => {}
        }
        const dateFormat = monthDayYearFormat
        const { contentType } = this.state
        const viewNoun = contentType === 'Stories' ? ' Reads' : ' Views'
        return function ({ date, viewCount }: Point) {
            const { scrollX, scrollY } = window
            const { left, top } = this.getBoundingClientRect()
            const g = select(this)
            const dot = g.select(`.${css.dotOrbit}`)
            const toolTipY = () =>
                top + scrollY - (toolTip && toolTip.node() ? toolTip.node()!.clientHeight : 0) - bubbleHeight
            const toolTipX = () =>
                left + scrollX - (toolTip && toolTip.node() ? toolTip.node()!.clientWidth : 0) / 2 + dotRadius
            const formattedDate = dateFormat(date)
            const template = `
                <p class="${css.toolTipTitle}">${formattedDate}</p>
                <p class="${css.toolTipViews}">${numberFormat(Math.floor(viewCount))} ${viewNoun}</p>
            `
            g.transition().duration(250).style('opacity', 1)
            dot.transition().duration(200).attr('r', dotRadius)
            if (toolTip) {
                toolTip
                    .html(template)
                    .style('left', `${toolTipX()}px`)
                    .style('top', `${toolTipY()}px`)
                    .style('visibility', 'visible')
                    .transition()
                    .duration(200)
                    .style('opacity', 1)
            }
        }
    }
    hideToolTip(toolTip: Selection<HTMLDivElement, unknown, HTMLElement, unknown> | null) {
        return function () {
            const g = select(this)
            const dot = g.select(`.${css.dotOrbit}`)
            g.transition().duration(500).style('opacity', 0)
            dot.transition().duration(500).attr('r', 0)
            if (toolTip) {
                toolTip
                    .transition()
                    .duration(500)
                    .style('opacity', 0)
                    .on('end', function () {
                        select(this).style('visibility', 'hidden')
                    })
            }
        }
    }
    render() {
        const { isFetching, contentType } = this.state
        const viewNoun = contentType === 'Stories' ? ' Reads' : ' Views'
        return (
            <div className={(css as { graph: string }).graph}>
                <Loader isFetching={isFetching} />
                <h1 className={(css as { lineTitle: string }).lineTitle}>
                    Total {singularContentTypes[contentType]} {viewNoun}
                </h1>
                <div className={css.chartContainer} ref={(el) => (this.container = el)}>
                    <svg
                        className={css.chart}
                        ref={(el) => {
                            if (el) {
                                this.svg = select(el)
                            } else {
                                this.svg = null
                            }
                        }}
                    />
                </div>
            </div>
        )
    }
}

const GraphAnalytics = onlyUpdateForKeys(['activeTimeSeries', 'dateRange', 'contentType'])(GraphAnalyticsBase)

export default GraphAnalytics
